import React from 'react';
import { Questionmark } from 'components/Icons/Icons';
import './OmnigrowTooltip.scss';
import { HorizontalDivider } from 'components/HorizontalDivider/HorizontalDivider';
import { routes } from '../../routing/routes';
import {
  Egg,
  Gribble,
  getPassivePoints,
  PASSIVE_EGG_OMNIGROW,
  PASSIVE_PAIRED_EGG_OMNIGROW,
  PASSIVE_GRIBBLE_OMNIGROW,
} from 'contexts/EggContext';
import { EggBreakerPair } from 'contexts/EggBreakerPairContext';
import { useUserPoints } from 'contexts/UserPointsContext';
import { useUserReferral, POINTS_PER_REFERRAL } from 'contexts/UserReferralContext';
import { OmnigrowPassiveTimer } from 'components/Omnigrow/PassiveTimer';
import { FLAGS, useFlags } from 'hooks/useFlags';

export const OmnigrowTooltip = () => {
  return (
    <div className="omnigrow-wrapper">
      <Questionmark />
      <div className="omnigrow-tooltip">
        <h4>How to Collect OmniGrow</h4>
        <HorizontalDivider />
        <div className="omnigrow-step-wrapper">
          <div className="omnigrow-step-title">
            <div className="omnigrow-step-number">1</div>
            <div className="omnigrow-step-name">
              <h5>Play Twilight Shift</h5>
            </div>
          </div>
          <div className="omnigrow-step-description">
            Download, install, and play the Twilight Shift game <a href={routes.launcher()}>here</a>
          </div>
          <HorizontalDivider />
        </div>
        <div className="omnigrow-step-wrapper">
          <div className="omnigrow-step-title">
            <div className="omnigrow-step-number">2</div>
            <div className="omnigrow-step-name">
              <h5>Explore Adrian&apos;s Story</h5>
            </div>
          </div>
          <div className="omnigrow-step-description">
            Go to <a href={routes.stories()}>Story</a> and read Adrian&apos;s history with DuskTech
          </div>
          <HorizontalDivider />
        </div>
        <div className="omnigrow-step-wrapper">
          <div className="omnigrow-step-title">
            <div className="omnigrow-step-number">3</div>
            <div className="omnigrow-step-name">
              <h5>Incubate and Pair your Eggs</h5>
            </div>
          </div>
          <div className="omnigrow-step-description">
            Incubate your Eggs and pair them any Breakers NFTs you own for extra bonuses
          </div>
          <HorizontalDivider />
        </div>
        <div className="omnigrow-step-wrapper">
          <div className="omnigrow-step-title">
            <div className="omnigrow-step-number">4</div>
            <div className="omnigrow-step-name">
              <h5>Complete Missions</h5>
            </div>
          </div>
          <div className="omnigrow-step-description">
            Go to <a href={routes.missions()}>Missions</a> to review and complete the available
            tasks
          </div>
        </div>
      </div>
    </div>
  );
};

export const NoOmnigrowTooltip = () => {
  return (
    <div className="omnigrow-wrapper">
      <Questionmark />
      <div className="omnigrow-tooltip">
        <h4>How to Collect OmniGrow</h4>
        <HorizontalDivider />
        <div className="omnigrow-step-wrapper">
          <div className="omnigrow-step-title">
            <div className="omnigrow-step-number">1</div>
            <div className="omnigrow-step-name">
              <h5>Refer a Friend</h5>
            </div>
          </div>
          <div className="omnigrow-step-description">
            Tell your friend about us and have them play our games
          </div>
          <HorizontalDivider />
        </div>
        <div className="omnigrow-step-wrapper">
          <div className="omnigrow-step-title">
            <div className="omnigrow-step-number">2</div>
            <div className="omnigrow-step-name">
              <h5>Incubate and Pair your Eggs and Gribbles</h5>
            </div>
          </div>
          <div className="omnigrow-step-description">
            Incubate your Eggs and Gribbles and pair them with any DuskBreaker NFTs you own for
            extra bonuses
          </div>
        </div>
      </div>
    </div>
  );
};

type OmnigrowSummaryProps = {
  pairs: EggBreakerPair[];
  eggs?: Egg[];
  gribbles?: Gribble[];
  omnigrow: number;
};

const POINTS_HISTORY_EVENTS = {
  story: 'Read Adrian’s Sanctuary',
  weekly: 'Weekly Mission',
  game: 'Twilight Shift Gameplay',
};

export const OmnigrowSummary = ({ pairs, eggs, gribbles, omnigrow }: OmnigrowSummaryProps) => {
  const eggCount = eggs?.length || 0;
  const gribblesCount = gribbles?.length || 0;
  const pairsCount = pairs.length;
  const { data } = useUserPoints();
  const { isFlagEnabled } = useFlags();
  const storySummary = data?.summary.find((event) => event.name === POINTS_HISTORY_EVENTS.story);
  const weeklySummary = data?.summary.find((event) => event.name === POINTS_HISTORY_EVENTS.weekly);
  const gameSummary = data?.summary.find((event) => event.name === POINTS_HISTORY_EVENTS.game);

  const passiveOmnigrowCount =
    PASSIVE_EGG_OMNIGROW * eggCount +
    PASSIVE_GRIBBLE_OMNIGROW * gribblesCount +
    PASSIVE_PAIRED_EGG_OMNIGROW * pairsCount;

  return (
    <div className="omnigrow-wrapper">
      <Questionmark />
      <div className="omnigrow-summary">
        <dl className="omnigrow-tooltip" id="omnigrow-summary-tooltip">
          <h4>OmniGrow Summary</h4>
          <div>
            <dt>OmniGrow Available</dt>
            <dd>{omnigrow}</dd>
          </div>
          <div>
            <dt>Total OmniGrow</dt>
            <dd>{data?.totalPoints}</dd>
          </div>
          <HorizontalDivider className="horizontal-divider-100" />
          {passiveOmnigrowCount > 0 && (
            <>
              <div>
                <dt>Passive</dt>
                <dd>+{passiveOmnigrowCount} / Day</dd>
              </div>
              <HorizontalDivider className="horizontal-divider-20" />
            </>
          )}
          {eggCount > 0 && (
            <div>
              <dt>Hold Gribble Egg(s) x{eggCount}</dt>
              <dd>+{PASSIVE_EGG_OMNIGROW * eggCount} / Day</dd>
            </div>
          )}
          {gribblesCount > 0 && (
            <div>
              <dt>Hold Gribble(s) x{gribblesCount}</dt>
              <dd>+{PASSIVE_GRIBBLE_OMNIGROW * gribblesCount} / Day</dd>
            </div>
          )}
          {pairsCount > 0 && (
            <div>
              <dt>Paired Breaker(s) x{pairsCount}</dt>
              <dd>+{PASSIVE_PAIRED_EGG_OMNIGROW * pairsCount} / Day</dd>
            </div>
          )}
          <OmnigrowPassiveTimer />
          <HorizontalDivider />
          {!isFlagEnabled(FLAGS.hatchening) && (
            <>
              <div>
                <dt>Active</dt>
              </div>
              <HorizontalDivider className="horizontal-divider-20" />

              <ReferralStats />

              {gameSummary ? (
                <div>
                  <dt>
                    Play <a href={routes.launcher()}>Game Missions</a> x{gameSummary.completedTimes}
                  </dt>
                  <dd>+{gameSummary.points}</dd>
                </div>
              ) : (
                <div>
                  <dt className="mb-2">
                    Play <a href={routes.launcher()}>Twilight Shift</a>
                  </dt>
                </div>
              )}
              {storySummary ? (
                <div>
                  <dt>
                    Read <a href={routes.stories()}>Adrian&apos;s Sanctuary</a> x
                    {storySummary.completedTimes}
                  </dt>
                  <dd>+{storySummary.points}</dd>
                </div>
              ) : (
                <div>
                  <dt className="mb-2">
                    Explore <a href={routes.stories()}>Adrian&apos;s Sanctuary</a>
                  </dt>
                </div>
              )}
              {weeklySummary ? (
                <div>
                  <dt className="mb-2">
                    <a href={routes.missions()}>Weekly Missions</a> x{weeklySummary.completedTimes}
                  </dt>
                  <dd>+{weeklySummary.points}</dd>
                </div>
              ) : (
                <div>
                  <dt>
                    Complete Weekly <a href={routes.missions()}>Missions</a>
                  </dt>
                </div>
              )}

              <HorizontalDivider />
            </>
          )}
          <span className="summary-description">
            {isFlagEnabled(FLAGS.hatchening)
              ? 'The passive section reflects your total OmniGrow collected'
              : 'The passive and active sections reflect your total OmniGrow collected'}
          </span>
        </dl>
      </div>
    </div>
  );
};

export type TooltipProps = {
  paired: boolean;
};

export const PassiveOmnigrowTooltip = ({ paired }: TooltipProps) => {
  return (
    <div className="omnigrow-wrapper">
      <Questionmark />

      <div className="passive-omnigrow-tooltip">
        <dl className="omnigrow-tooltip">
          <dt>Passive </dt>
          <dd>+{getPassivePoints(paired)}/Day</dd>
          <HorizontalDivider className="horizontal-divider-100" />
          <dt>Holding Egg </dt>
          <dd>+{PASSIVE_EGG_OMNIGROW}/Day</dd>
          {paired && (
            <>
              <HorizontalDivider className="horizontal-divider-20" />
              <dt>Paired Breaker </dt>
              <dd>+{PASSIVE_PAIRED_EGG_OMNIGROW}/Day</dd>
            </>
          )}
        </dl>
      </div>
    </div>
  );
};

const ReferralStats = () => {
  const { rewards } = useUserReferral();

  if (!rewards.data) {
    return null;
  }

  const { pointsDetails } = rewards.data;
  const { pointsReceivedAsReferrer, pointsReceivedAsReferree } = pointsDetails;

  return (
    <>
      {pointsReceivedAsReferrer > 0 && (
        <div>
          <dt className="mb-2">
            <a href={routes.referral()}>Refer a Friend</a> x
            {pointsReceivedAsReferrer / POINTS_PER_REFERRAL}
          </dt>
          <dd>+{pointsReceivedAsReferrer}</dd>
        </div>
      )}

      {pointsReceivedAsReferree > 0 && (
        <div>
          <dt className="mb-2">
            <a href={routes.referral()}>Referred by a Friend</a> x
            {pointsReceivedAsReferree / POINTS_PER_REFERRAL}
          </dt>
          <dd>+{pointsReceivedAsReferree}</dd>
        </div>
      )}
    </>
  );
};
